/* eslint-disable camelcase */
import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import user from 'js/lib/user';

import { color } from '@coursera/coursera-ui';
import { SvgApple, SvgCircleWarning } from '@coursera/coursera-ui/svg';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import { delink, link } from 'bundles/third-party-auth/actions/AccountLinkActions';
import AccountLinkStore from 'bundles/third-party-auth/stores/AccountLinkStore';
import {
  USER_PASSWORD_MAX_LENGTH,
  USER_PASSWORD_MIN_LENGTH,
  USER_PASSWORD_REGEX_PATTERN,
} from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/third-party-auth';

const DELINK = 'delink';

type Context = {
  executeAction: () => void;
  getStore: () => void;
};

type SuccessCodes = 'link' | 'delink';

type Props = {
  currentAccountType: string;
  loading: boolean;
  linked?: {
    facebook?: boolean;
    apple?: boolean;
    saml?: boolean;
    jwt?: string;
    google?: boolean;
  };
  requirePassword: boolean;
  errorCode?: string;
  successCode?: SuccessCodes;
};

class AccountLinkApple extends React.Component<Props, {}, Context> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof AccountLinkApple.contextTypes>;

  handleLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { executeAction } = this.context;

    e.preventDefault();
    executeAction(link, {
      accountType: 'apple',
      currentAccountType: 'apple',
    });
  };

  handleDelink = (e: React.FormEvent<HTMLFormElement>) => {
    const { executeAction } = this.context;

    e.preventDefault();
    let payload: {
      currentAccountType: string;
      accountType: string;
      password?: string;
    } = {
      accountType: 'apple',
      currentAccountType: 'apple',
    };

    if (this.passwordRef) {
      payload = { ...payload, password: this.passwordRef.value };
    }

    executeAction(delink, payload);
  };

  getErrorMessage(code: string) {
    const { linked } = this.props;
    const appleIsLinked = linked?.apple;

    if (!this.isCurrentAccountApple()) {
      return null;
    }

    if (code === 'accountIsAlreadyLinked') {
      return _t('This Apple account has already been linked to another Coursera account.');
    } else if (code === 'accountNotLinked') {
      return _t('Your Coursera account is not currently linked to any Apple accounts.');
    } else if (code === 'noPasswordSupplied') {
      return _t('Please enter a valid password above.');
    } else if (appleIsLinked && code === 'Password is not sufficiently complex') {
      return _t(
        'Password must be between #{minLength} and #{maxLength} characters. For your security, avoid passwords like qwerty123 or password123.',
        {
          minLength: USER_PASSWORD_MIN_LENGTH,
          maxLength: USER_PASSWORD_MAX_LENGTH,
        }
      );
    } else if (appleIsLinked) {
      return _t("Uh oh! We weren't able to unlink your account. Please try again later.");
    } else {
      return _t("Uh oh! We weren't able to link your account. Please try again later.");
    }
  }

  isCurrentAccountApple = () => {
    const { currentAccountType } = this.props;

    return currentAccountType === 'apple';
  };

  passwordRef?: HTMLInputElement = undefined;

  renderErrorMessage() {
    const { errorCode } = this.props;
    if (!this.isCurrentAccountApple()) {
      return null;
    } else if (errorCode) {
      return (
        <div role="alert" className="rc-StatusMessage error m-t-1s">
          {this.getErrorMessage(errorCode)}
        </div>
      );
    } else {
      return null;
    }
  }

  renderSuccessMessage() {
    const { linked, successCode } = this.props;
    const appleIsLinked = linked?.apple;

    if (!this.isCurrentAccountApple()) {
      return null;
    }

    const messages = {
      link: _t('Successfully linked your Apple account.'),
      delink: _t('Successfully unlinked your Apple account.'),
    };
    let message: string | undefined;
    if (successCode && appleIsLinked !== null) {
      message = messages[successCode];
    }

    if (message) {
      return (
        <div role="alert" className="rc-StatusMessage success m-t-1s">
          {message}
        </div>
      );
    } else {
      return null;
    }
  }

  renderAppleButtonDisabledMessage() {
    const { linked } = this.props;

    const samlIsLinked = linked?.saml;
    const jwtIsLinked = linked?.jwt;
    const facebookIsLinked = linked?.facebook;
    const googleIsLinked = linked?.google;

    let message: string | null = null;

    if (samlIsLinked || jwtIsLinked) {
      message = _t('Unlink your organization account to log in with Apple.');
    } else if (facebookIsLinked) {
      message = _t('Unlink your Facebook account to log in with Apple.');
    } else if (googleIsLinked) {
      message = _t('Unlink your Google account to log in with Apple.');
    }

    if (message !== null) {
      return (
        <div>
          <p className="m-x-1">{message}</p>
        </div>
      );
    } else {
      return null;
    }
  }

  renderBody() {
    const { linked, loading, successCode } = this.props;

    const userData = user.get();

    const hasPrivateRelayEmail = userData?.email_address.indexOf('privaterelay.appleid.com') !== -1;

    const shouldShowEmailUpdateWarning = successCode === DELINK && hasPrivateRelayEmail;

    const appleIsLinked = linked?.apple;
    const facebookIsLinked = linked?.facebook;
    const googleIsLinked = linked?.google;
    const jwtIsLinked = linked?.jwt;
    const samlIsLinked = linked?.saml;

    if (appleIsLinked) {
      return (
        <div>
          <p>{_t('Your Coursera account is currently linked to your Apple account.')}</p>
          <form onSubmit={this.handleDelink}>
            {this.props.requirePassword && (
              <div className="settings-input-wrapper">
                <label htmlFor="password" className="error">
                  {_t('Please set a password for future logins to your Coursera account.')}
                </label>
                <input
                  ref={(c: HTMLInputElement) => {
                    this.passwordRef = c;
                  }}
                  id="password"
                  type="password"
                  required
                  pattern={USER_PASSWORD_REGEX_PATTERN}
                  title={_t('Password must be between #{minLength} and #{maxLength} characters', {
                    minLength: USER_PASSWORD_MIN_LENGTH,
                    maxLength: USER_PASSWORD_MAX_LENGTH,
                  })}
                />
              </div>
            )}
            <div className="horizontal-box">
              <div>
                <TrackedCdsButton
                  trackingName="delink_apple"
                  trackingData={{ account_type: 'apple' }}
                  variant="secondary"
                  disabled={
                    samlIsLinked ||
                    !!jwtIsLinked ||
                    facebookIsLinked ||
                    googleIsLinked ||
                    (this.isCurrentAccountApple() && loading)
                  }
                  type="submit"
                  loading={this.isCurrentAccountApple() && loading}
                >
                  {_t('Unlink my Apple Account')}
                </TrackedCdsButton>
              </div>
              {this.renderSuccessMessage()}
              {this.renderErrorMessage()}
            </div>
          </form>
        </div>
      );
    } else {
      return (
        <div>
          <p>{_t('Enable one-click login and receive more personalized course recommendations.')}</p>
          <div className="horizontal-box">
            <div>
              <TrackedCdsButton
                trackingName="link_apple"
                trackingData={{ account_type: 'apple' }}
                onClick={this.handleLink}
                variant="secondary"
                disabled={
                  samlIsLinked ||
                  !!jwtIsLinked ||
                  facebookIsLinked ||
                  googleIsLinked ||
                  (this.isCurrentAccountApple() && loading)
                }
                loading={this.isCurrentAccountApple() && loading}
              >
                {_t('Link my Apple Account')}
              </TrackedCdsButton>
            </div>
            {this.renderSuccessMessage()}
            {this.renderErrorMessage()}
            {this.renderAppleButtonDisabledMessage()}
          </div>
          <div>
            {shouldShowEmailUpdateWarning && (
              <div className="email-update-warning">
                <SvgCircleWarning size={24} color={color.warning} />
                <span>{_t('Please update your email at the top of this page.')}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  render() {
    return (
      <div className="rc-LinkApple account-link-option">
        <h3 className="account-link-option__header">
          <SvgApple size={14} color={color.black} aria-hidden />
          {_t('Apple')}
        </h3>
        {this.renderBody()}
      </div>
    );
  }
}

// @ts-expect-error TODO(Igor): update connectToStores definition to support this syntax
export default connectToStores(AccountLinkApple, [AccountLinkStore], (storeInstances) => {
  // @ts-expect-error FIXME
  return storeInstances[AccountLinkStore.storeName].dehydrate();
});
